
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { X, Plus } from "lucide-react";
import { useAdminCountryAssignments } from "@/hooks/admin/countries/useAdminCountryAssignments";
import { useCountries } from "@/hooks/admin/countries/useCountries";
import type { Database } from "@/integrations/supabase/types";

type UserProfile = Database['public']['Tables']['profiles']['Row'];

interface AdminCountryAssignmentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  admin: UserProfile | null;
}

const AdminCountryAssignmentDialog = ({ 
  open, 
  onOpenChange, 
  admin 
}: AdminCountryAssignmentDialogProps) => {
  const [selectedCountry, setSelectedCountry] = useState<string>("");
  const [adminAssignments, setAdminAssignments] = useState<any[]>([]);
  
  const { countries } = useCountries();
  const { 
    assignments, 
    getAdminAssignments, 
    assignCountry, 
    removeAssignment,
    isAssigning,
    isRemoving
  } = useAdminCountryAssignments();

  // Load admin's current assignments when dialog opens or admin changes
  useEffect(() => {
    if (open && admin?.id) {
      console.log('Loading assignments for admin:', admin.id);
      getAdminAssignments(admin.id).then((assignments) => {
        console.log('Loaded assignments:', assignments);
        setAdminAssignments(assignments);
      });
    }
  }, [open, admin?.id, getAdminAssignments]);

  // Update assignments when global assignments change
  useEffect(() => {
    if (admin?.id && assignments.length > 0) {
      console.log('Global assignments updated:', assignments);
      const adminSpecificAssignments = assignments.filter(
        assignment => assignment.admin_id === admin.id
      );
      console.log('Admin specific assignments:', adminSpecificAssignments);
      setAdminAssignments(adminSpecificAssignments);
    }
  }, [assignments, admin?.id]);

  const handleAssignCountry = async () => {
    if (selectedCountry && admin?.id) {
      console.log('Assigning country:', selectedCountry, 'to admin:', admin.id);
      try {
        await assignCountry({ adminId: admin.id, countryCode: selectedCountry });
        setSelectedCountry("");
        // Refresh the assignments after successful assignment
        const updatedAssignments = await getAdminAssignments(admin.id);
        setAdminAssignments(updatedAssignments);
      } catch (error) {
        console.error('Error assigning country:', error);
      }
    }
  };

  const handleRemoveAssignment = async (assignmentId: string) => {
    console.log('Removing assignment:', assignmentId);
    try {
      await removeAssignment(assignmentId);
      // Refresh the assignments after successful removal
      if (admin?.id) {
        const updatedAssignments = await getAdminAssignments(admin.id);
        setAdminAssignments(updatedAssignments);
      }
    } catch (error) {
      console.error('Error removing assignment:', error);
    }
  };

  const availableCountries = countries.filter(country => 
    !adminAssignments.some(assignment => assignment.country_code === country.code)
  );

  if (!admin) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Manage Country Assignments - {admin.name}</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Current assignments */}
          <div>
            <h4 className="text-sm font-medium mb-2">Current Assignments</h4>
            {adminAssignments.length === 0 ? (
              <p className="text-sm text-muted-foreground">No countries assigned</p>
            ) : (
              <div className="space-y-2">
                {adminAssignments.map((assignment) => (
                  <div key={assignment.id} className="flex items-center justify-between">
                    <Badge variant="secondary">
                      {countries.find(c => c.code === assignment.country_code)?.name || assignment.country_code}
                    </Badge>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveAssignment(assignment.id)}
                      disabled={isRemoving}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Add new assignment */}
          <div>
            <h4 className="text-sm font-medium mb-2">Add Country Assignment</h4>
            <div className="flex gap-2">
              <Select value={selectedCountry} onValueChange={setSelectedCountry}>
                <SelectTrigger className="flex-1">
                  <SelectValue placeholder="Select country" />
                </SelectTrigger>
                <SelectContent>
                  {availableCountries.map((country) => (
                    <SelectItem key={country.code} value={country.code}>
                      {country.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                onClick={handleAssignCountry}
                disabled={!selectedCountry || isAssigning}
                size="sm"
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AdminCountryAssignmentDialog;
